// src/components/UI.jsx
// Shared reusable components

import React, { useState, useEffect, useRef } from 'react';

// ─── Status Badge ─────────────────────────────────────────────────────────────
const STATUS_MAP = {
  'Pending': { cls: 'pending', dot: '#94a3b8', label: 'Pending' },
  'Assigned': { cls: 'assigned', dot: '#3b82f6', label: 'Assigned' },
  'Picked Up': { cls: 'picked-up', dot: '#f59e0b', label: 'Picked Up' },
  'In Transit': { cls: 'in-transit', dot: '#8b5cf6', label: 'In Transit' },
  'Delivered': { cls: 'delivered', dot: '#22c55e', label: 'Delivered' },
  'Failed': { cls: 'failed', dot: '#ef4444', label: 'Failed' },
};

export function StatusBadge({ status }) {
  const cfg = STATUS_MAP[status] || STATUS_MAP['Pending'];
  return (
    <span className={`badge ${cfg.cls}`}>
      <span className="badge-dot" style={{ background: cfg.dot }} />
      {cfg.label}
    </span>
  );
}

// ─── Toast System ─────────────────────────────────────────────────────────────
let toastListeners = [];
export function toast(message, type = 'info') {
  const id = Date.now();
  toastListeners.forEach(fn => fn({ id, message, type }));
  return id;
}
toast.success = (msg) => toast(msg, 'success');
toast.error = (msg) => toast(msg, 'error');
toast.info = (msg) => toast(msg, 'info');

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const fn = (t) => {
      setToasts(prev => [...prev, t]);
      setTimeout(() => setToasts(prev => prev.filter(x => x.id !== t.id)), 4000);
    };
    toastListeners.push(fn);
    return () => { toastListeners = toastListeners.filter(f => f !== fn); };
  }, []);

  const icons = { success: '✅', error: '❌', info: 'ℹ️' };

  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`toast ${t.type}`}>
          <span>{icons[t.type]}</span>
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, footer, size = 'md' }) {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: size === 'lg' ? 700 : 520 }}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ─── Loading ──────────────────────────────────────────────────────────────────
export function Spinner({ size = 20 }) {
  return (
    <div
      className="loading-spinner"
      style={{ width: size, height: size, borderWidth: size > 24 ? 3 : 2.5 }}
    />
  );
}

export function LoadingOverlay({ message = 'Loading…' }) {
  return (
    <div className="loading-overlay">
      <Spinner size={32} />
      <span>{message}</span>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
export function EmptyState({ icon = '📭', title, subtitle, action }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">{icon}</div>
      <div className="empty-state-title">{title}</div>
      {subtitle && <p className="empty-state-sub">{subtitle}</p>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
export function ConfirmModal({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false, loading = false }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn btn-ghost btn-md" onClick={onClose}>Cancel</button>
          <button
            className={`btn btn-md ${danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <Spinner size={16} /> : confirmLabel}
          </button>
        </>
      }
    >
      <p style={{ fontSize: 14, color: 'var(--gray-600)', lineHeight: 1.6 }}>{message}</p>
    </Modal>
  );
}

// ─── Search Input ─────────────────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div style={{ position: 'relative', minWidth: 220 }}>
      <span style={{
        position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
        fontSize: 14, color: 'var(--gray-400)', pointerEvents: 'none'
      }}>🔍</span>
      <input
        className="form-input"
        style={{ paddingLeft: 34, width: '100%' }}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

// ─── Select / Filter ──────────────────────────────────────────────────────────
export function SelectFilter({ value, onChange, options, style }) {
  return (
    <select className="form-select" style={{ width: 'auto', ...style }} value={value} onChange={e => onChange(e.target.value)}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ─── QR Display ──────────────────────────────────────────────────────────────
export function QRDisplay({ value, size = 140 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!value || !canvasRef.current) return;
    import('qrcode').then(QRCode => {
      QRCode.toCanvas(canvasRef.current, value, {
        width: size,
        margin: 2,
        color: { dark: '#0a1628', light: '#ffffff' }
      });
    });
  }, [value, size]);

  return (
    <div style={{
      background: 'white', padding: 12, borderRadius: 10,
      display: 'inline-block', border: '1px solid var(--gray-200)'
    }}>
      <canvas ref={canvasRef} />
    </div>
  );
}